import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';

import { AuthenticationGuard } from '@app/core/guards/authentication/authentication.guard';
import { PermissionGuard } from '@app/core/guards/permission/permission.guard';
import { Version2Guard } from '@app/core/guards/version-2/version2-guard.guard';
import { ExistingUserGuard } from '@app/core/guards/existing-user/existing-user.guard';

import { PermissionEnum } from '@app/generated/graphql';
import { ShareGuard } from '../core/guards/share/share.guard';
import { onboardingGuard } from '@app/core/guards/onboarding/onboarding.guard';

export const routes: Routes = [
  {
    path: 'account',
    loadComponent: () =>
      import('./account.component').then((m) => m.AccountComponent),
    canActivate: [AuthenticationGuard],
    children: [
      // Used to showcase examples of components and funtionalities
      {
        path: 'component-examples',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesDemo,
        },
        loadChildren: () =>
          import('./examples/components/component-examples.routes').then(
            (m) => m.COMPONENT_EXAMPLES_ROUTES,
          ),
      },
      // Reference implementations ("patterns") showing how common tasks should be built
      {
        path: 'patterns',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesDemo,
        },
        loadChildren: () =>
          import('./patterns/patterns.routes').then((m) => m.PATTERNS_ROUTES),
      },
      {
        path: 'feature-examples',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesDemo,
        },
        loadChildren: () =>
          import('./examples/features/feature-examples.routes').then(
            (m) => m.FEATURE_EXAMPLES_ROUTES,
          ),
      },
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        pathMatch: 'full',
        canActivate: [PermissionGuard, Version2Guard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesDashboard,
        },
        loadChildren: () =>
          import('./dashboard/dashboard.routes').then(
            (m) => m.DASHBOARD_ROUTES,
          ),
      },
      {
        path: 'settings',
        pathMatch: 'full',
        canActivate: [PermissionGuard],
        data: {
          resourceId: PermissionEnum.PagesSettings,
        },
        loadChildren: () =>
          import('./settings/settings.routes').then((m) => m.SETTINGS_ROUTES),
      },
      {
        path: 'company',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesCompany,
        },
        loadChildren: () =>
          import('./company/company.routes').then((m) => m.COMPANY_ROUTES),
      },
      {
        path: 'order/:id',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesViewOrder,
        },
        loadChildren: () =>
          import('./view-order/view-order.routes').then(
            (m) => m.VIEW_ORDER_ROUTES,
          ),
      },
      {
        path: 'orders',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesOrders,
        },
        loadChildren: () =>
          import('./orders/order.routes').then((m) => m.ORDER_ROUTES),
      },
      {
        path: 'offers',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesOffers,
        },
        loadChildren: () =>
          import('./offers/offers.routes').then((m) => m.OFFERS_ROUTES),
      },
      {
        path: 'partners',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesPartners,
        },
        loadChildren: () =>
          import('./trade-partners/trade-partners.routes').then(
            (m) => m.TRADE_PARTNERS_ROUTES,
          ),
      },
      {
        path: 'estimations',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesEstimations,
        },
        loadChildren: () =>
          import('./estimation/estimation.routes').then(
            (m) => m.ESTIMATION_ROUTES,
          ),
      },
      {
        path: 'planning',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesPlanning,
        },
        loadChildren: () =>
          import('./planning/planning.routes').then((m) => m.PLANNING_ROUTES),
      },
      {
        path: 'pack-instructions',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesPackInstructions,
        },
        loadChildren: () =>
          import('./pack-instructions/pack-instructions.routes').then(
            (m) => m.PACK_INSTRUCTIONS_ROUTES,
          ),
      },
      {
        path: 'load-out-instructions',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesLoadOutInstructions,
        },
        loadChildren: () =>
          import('./load-out-instructions/load-out-instruction.routes').then(
            (m) => m.LOAD_OUT_INSTRUCTION_ROUTES,
          ),
      },
      {
        path: 'shipments',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesShipments,
        },
        loadChildren: () =>
          import('./shipments/shipment.routes').then((m) => m.SHIPMENT_ROUTES),
      },
      {
        path: 'air-freight-shipments',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesAirFreightShipments,
        },
        loadChildren: () =>
          import('./air-freight-shipments/air-freight-shipment.routes').then(
            (m) => m.AIR_FREIGHT_SHIPMENT_ROUTES,
          ),
      },
      {
        path: 'arrival-qc',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesClaims,
        },
        loadChildren: () =>
          import('./quality-and-compliance/arrival-qc.routes').then(
            (m) => m.ARRIVAL_QC_ROUTES,
          ),
      },
      {
        path: 'commercial-invoices',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesCommercialInvoices,
        },
        loadChildren: () =>
          import('./commercial-invoices/commercial-invoice.routes').then(
            (m) => m.COMMERCIAL_INVOICE_ROUTES,
          ),
      },
      {
        path: 'cash-flow',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          isLineItemBased: true,
          resourceId: PermissionEnum.PagesCashFlow,
        },
        loadChildren: () =>
          import('./cash-flow/cash-flow.routes').then(
            (m) => m.CASH_FLOW_ROUTES,
          ),
      },
      {
        path: 'finance',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesCashFlow,
        },
        loadChildren: () =>
          import('./finance/finance.routes').then((m) => m.FINANCE_ROUTES),
      },
      {
        path: 'commercial-terms',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesCommercialTerms,
        },
        loadChildren: () =>
          import('./commercial-terms/commercial-terms.routes').then(
            (m) => m.COMMERCIAL_TERMS_ROUTES,
          ),
      },
      {
        path: 'financial-invoices',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesCustomerInvoices,
        },
        loadChildren: () =>
          import('./financial-invoices/financial-invoice.routes').then(
            (m) => m.FINANCIAL_INVOICE_ROUTES,
          ),
      },
      {
        path: 'vendor-invoices',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesVendorInvoices,
        },
        loadChildren: () =>
          import('./vendor-invoices/vendor-invoices.routes').then(
            (m) => m.VENDOR_INVOICES_ROUTES,
          ),
      },
      {
        path: 'default-costs',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesDefaultCosts,
        },
        loadChildren: () =>
          import('./default-costs/default-costs.routes').then(
            (m) => m.DEFAULT_COSTS_ROUTES,
          ),
      },
      {
        path: 'finance-rules',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesFinanceRuleManagement,
        },
        loadChildren: () =>
          import('./finance-rules/finance-rules.routes').then(
            (m) => m.FINANCE_RULES_ROUTES,
          ),
      },
      {
        path: 'payments',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesPayments,
        },
        loadChildren: () =>
          import('./payments/payments.routes').then((m) => m.PAYMENTS_ROUTES),
      },
      {
        path: 'data-exports',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesDataExports,
        },
        loadChildren: () =>
          import('./data-exports/data-exports.routes').then(
            (m) => m.DATA_EXPORTS_ROUTES,
          ),
      },
      {
        path: 'insights',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesInsights,
        },
        loadChildren: () =>
          import('./insights/index/index.routes').then((m) => m.INDEX_ROUTES),
      },
      {
        path: 'incoterm-calculator',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesIncotermCalculator,
        },
        loadChildren: () =>
          import('./net-returns-calculator/net-returns-calculator.routes').then(
            (m) => m.NET_RETURNS_CALCULATOR_ROUTES,
          ),
      },
      {
        path: 'insights/report-group/:id',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesInsights,
        },
        loadChildren: () =>
          import('./insights/view-report-group/view.routes').then(
            (m) => m.VIEW_ROUTES,
          ),
      },
      {
        path: 'insights/:id',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesInsights,
        },
        loadChildren: () =>
          import('./insights/view-metabase/view.routes').then(
            (m) => m.VIEW_ROUTES,
          ),
      },
      {
        path: 'stock',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesStock,
        },
        loadChildren: () =>
          import('./stock/stock.routes').then((m) => m.STOCK_ROUTES),
      },
      {
        path: 'season-setup',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard, onboardingGuard],
        data: {
          resourceId: PermissionEnum.PagesMasterfiles,
        },
        loadChildren: () =>
          import('./season-setup/season-setup.routes').then(
            (m) => m.SEASON_SETUP_ROUTES,
          ),
      },
      {
        path: 'notification-preferences',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesSettings,
        },
        loadChildren: () =>
          import(
            './notification-preferences/notification-preferences.routes'
          ).then((m) => m.NOTIFICATION_PREFERENCES_ROUTES),
      },
      {
        path: 'packhouse-confirmation',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.MasterfilesPackhouses,
        },
        loadChildren: () =>
          import(
            './packhouse-confirmation-dashboard/packhouse-confirmation-dashboard.routes'
          ).then((m) => m.PACKHOUSE_CONFIRMATION_DASHBOARD_ROUTES),
      },
      {
        path: 'data-confirmation-dashboard',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesMasterfiles,
        },
        loadChildren: () =>
          import(
            './data-confirmation-dashboard/data-confirmation-dasboard.routes'
          ).then((m) => m.DATA_CONFIRMATION_DASBOARD_ROUTES),
      },
      {
        path: 'supply-chain-confirmation',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesMasterfiles,
        },
        loadChildren: () =>
          import(
            './supply-chain-confirmation/supply-chain-confirmation.routes'
          ).then((m) => m.SUPPLY_CHAIN_CONFIRMATION_ROUTES),
      },
      {
        path: 'masterfiles/farm-varieties',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.MasterfilesFarmVarieties,
        },
        loadChildren: () =>
          import('./masterfiles/farm-varieties/farm-varieties.routes').then(
            (m) => m.FARM_VARIETIES_ROUTES,
          ),
      },
      {
        path: 'masterfiles/orchards',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.MasterfilesOrchards,
        },
        loadChildren: () =>
          import('./masterfiles/orchards/orchards.routes').then(
            (m) => m.ORCHARDS_ROUTES,
          ),
      },
      {
        path: 'masterfiles/food-business-operators',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.MasterfilesFoodBusinessOperators,
        },
        loadChildren: () =>
          import(
            './masterfiles/food-business-operators/food-business-operators.routes'
          ).then((m) => m.FOOD_BUSINESS_OPERATORS_ROUTES),
      },
      {
        path: 'masterfiles/carton-conversions',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.MasterfilesCartonConversions,
        },
        loadChildren: () =>
          import(
            './masterfiles/carton-conversions/carton-conversions.routes'
          ).then((m) => m.CARTON_CONVERSIONS_ROUTES),
      },
      {
        path: 'masterfiles/errors',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.MasterfilesErrors,
        },
        loadChildren: () =>
          import('./masterfiles/errors/errors.routes').then(
            (m) => m.ERRORS_ROUTES,
          ),
      },
      {
        path: 'masterfiles/packhouse-manager',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.MasterfilesPackhouses,
        },
        loadChildren: () =>
          import(
            './masterfiles/packhouse-manager/packhouse-manager.routes'
          ).then((m) => m.PACKHOUSE_MANAGER_ROUTES),
      },
      {
        path: 'masterfiles/supply-chain-setup',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesMasterfiles,
        },
        loadChildren: () =>
          import(
            './masterfiles/supply-chain-setup/supply-chain-setup.routes'
          ).then((m) => m.SUPPLY_CHAIN_SETUP_ROUTES),
      },
      {
        path: '404',
        loadComponent: () =>
          import('./four-oh-four/four-oh-four.component').then(
            (m) => m.FourOhFourComponent,
          ),
        data: {},
      },
      {
        path: '500',
        loadComponent: () =>
          import('./server-error/server-error.component').then(
            (m) => m.ServerErrorComponent,
          ),
        data: {},
      },
      {
        path: '**',
        redirectTo: '/account/404',
        data: {},
      },
    ],
  },
  {
    path: 'shared/insights/report-group/:id',
    pathMatch: 'full',
    canActivate: [ShareGuard],
    loadChildren: () =>
      import('./insights/view-report-group/view.routes').then(
        (m) => m.VIEW_ROUTES,
      ),
  },
  {
    path: 'shared/insights/report-group-cycle/:id',
    pathMatch: 'full',
    canActivate: [ShareGuard],
    loadChildren: () =>
      import('./insights/report-group-cycle/report-group-cycle.routes').then(
        (m) => m.REPORT_GROUP_CYCLE_ROUTES,
      ),
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class AccountRoutingModule {}
